import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { FileText, Euro, Calendar, Eye, Download, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoicesManager = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [invoiceHtml, setInvoiceHtml] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    payment_id: '',
    user_address: '',
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchPayments();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/invoices`, getAuthHeaders());
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`, getAuthHeaders());
      // Filter only completed payments without invoices
      const completedPayments = response.data.filter(p => p.status === 'completed');
      setPayments(completedPayments);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/admin/invoice/generate`,
        formData,
        getAuthHeaders()
      );

      if (response.data.success) {
        toast.success('Facture générée avec succès');
        setDialogOpen(false);
        fetchInvoices();
        fetchPayments();
        setFormData({ payment_id: '', user_address: '', notes: '' });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Erreur lors de la génération de la facture');
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(
        `${API}/admin/invoice/${invoiceId}/html`,
        getAuthHeaders()
      );
      setInvoiceHtml(response.data.html);
      setPreviewDialog(true);
    } catch (error) {
      console.error('Error fetching invoice HTML:', error);
      toast.error('Erreur lors du chargement de la facture');
    }
  };

  const handleDownloadInvoice = (invoice) => {
    // Create a blob from the HTML and download it
    const blob = new Blob([invoiceHtml], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facture-${invoice.invoice_number}.html`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Facture téléchargée');
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { bg: 'bg-slate-100 text-slate-800', label: 'Brouillon' },
      sent: { bg: 'bg-blue-100 text-blue-800', label: 'Envoyée' },
      paid: { bg: 'bg-green-100 text-green-800', label: 'Payée' },
      overdue: { bg: 'bg-red-100 text-red-800', label: 'En retard' },
      cancelled: { bg: 'bg-slate-100 text-slate-800', label: 'Annulée' }
    };
    const variant = variants[status] || variants.draft;
    return (
      <Badge className={`${variant.bg} hover:${variant.bg}`}>
        {variant.label}
      </Badge>
    );
  };

  const filteredInvoices = statusFilter === 'all'
    ? invoices
    : invoices.filter(inv => inv.status === statusFilter);

  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.status === 'paid').length,
    pending: invoices.filter(i => i.status === 'sent').length,
    totalAmount: invoices
      .filter(i => i.status === 'paid')
      .reduce((sum, i) => sum + i.total_amount, 0)
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">Gestion des Factures</h1>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-20 bg-slate-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion des Factures</h1>
          <p className="text-slate-600 mt-2">{invoices.length} factures générées</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-blue-900 hover:bg-blue-800"
        >
          <Plus className="h-4 w-4 mr-2" />
          Générer une facture
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total factures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Payées</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.paid}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">En attente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Montant total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-900">{stats.totalAmount.toFixed(2)} €</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Filtrer par statut :</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="sent">Envoyées</SelectItem>
                <SelectItem value="paid">Payées</SelectItem>
                <SelectItem value="overdue">En retard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <div className="space-y-4">
        {filteredInvoices.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">Aucune facture trouvée</p>
            </CardContent>
          </Card>
        ) : (
          filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-900">
                        {invoice.invoice_number}
                      </h3>
                      {getStatusBadge(invoice.status)}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                      <div>
                        <span className="font-medium">Client :</span> {invoice.user_name}
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>{invoice.service_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Euro className="h-4 w-4" />
                        <span className="font-bold">{invoice.total_amount.toFixed(2)} € TTC</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(invoice.issue_date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    {invoice.paid_date && (
                      <p className="text-xs text-green-600">
                        Payée le {new Date(invoice.paid_date).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewInvoice(invoice.id)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Voir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Generate Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Générer une facture</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateInvoice} className="space-y-4">
            <div>
              <Label htmlFor="payment">Paiement *</Label>
              <Select
                value={formData.payment_id}
                onValueChange={(value) => setFormData({ ...formData, payment_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un paiement complété" />
                </SelectTrigger>
                <SelectContent>
                  {payments.filter(p => !invoices.some(i => i.payment_id === p.id)).map((payment) => (
                    <SelectItem key={payment.id} value={payment.id}>
                      {payment.user_name} - {payment.service_plan.replace('_', ' ')} - {payment.amount}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Seuls les paiements complétés sans facture sont affichés
              </p>
            </div>

            <div>
              <Label htmlFor="user_address">Adresse client (optionnel)</Label>
              <Input
                id="user_address"
                value={formData.user_address}
                onChange={(e) => setFormData({ ...formData, user_address: e.target.value })}
                placeholder="123 Rue Example, 75001 Paris"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes internes"
              />
            </div>

            <div className="flex gap-4 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                Générer la facture
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Aperçu de la facture</span>
              <Button
                size="sm"
                onClick={() => {
                  const invoice = invoices.find(i => i.id);
                  if (invoice) handleDownloadInvoice(invoice);
                }}
                className="bg-blue-900 hover:bg-blue-800"
              >
                <Download className="h-4 w-4 mr-2" />
                Télécharger
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div
            className="border rounded-lg p-4 overflow-auto max-h-[70vh] bg-white"
            dangerouslySetInnerHTML={{ __html: invoiceHtml }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesManager;
